/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import TestIds from '../../../../testIds';
import {
  selectAllFarmerAccounts,
  selectUserContactDetails,
  selectIsCEUser,
  selectLoginEmail,
} from '../../../azure-b2c/storage';
import { ACCOUNT_PROFILE_ROUTE } from '../../account.routes';
import { MarginStyledBox } from '../../../../styled/box';
import InitialsCircle from '../InitialsCircle/InitialsCircle';

import './ContactProfileHeader.scss';
import { colors } from '@farmlands/mui-theme';
import { getStringFromArray } from '../../../../util/formatting';

const ContactProfileHeader: FC = (): JSX.Element | undefined => {
  const farmlandsAccounts = useSelector(selectAllFarmerAccounts);
  const accountEmail = useSelector(selectLoginEmail);
  const isCEUser = useSelector(selectIsCEUser);
  const userDetails = useSelector(selectUserContactDetails);
  const contactName = getStringFromArray([userDetails?.firstName, userDetails?.lastName]);

  if (farmlandsAccounts) {
    const content = (
      <MarginStyledBox>
        <Stack spacing='0.5rem' alignItems='center'>
          <InitialsCircle fullName={[userDetails?.firstName, userDetails?.lastName]} />

          <Stack alignItems='center'>
            <Box
              className='fl-contact-profile-header__contact-name'
              data-testid={TestIds.feat.account.userAccount.contactProfileHeader.contactName}
            >
              {contactName}
            </Box>

            <Box data-testid={TestIds.feat.account.userAccount.contactProfileHeader.email}>
              {accountEmail}
            </Box>
          </Stack>

          {!isCEUser ? (
            <Typography
              className='fl-contact-profile-header__view-profile'
              variant='defaultUnderline'
            >
              View profile
            </Typography>
          ) : null}
        </Stack>
      </MarginStyledBox>
    );

    if (isCEUser) {
      return (
        <Box
          className='fl-mobile-safe-area-support'
          sx={{
            backgroundColor: colors.primary50,
            padding: '12px',
          }}
          data-testid={TestIds.feat.account.userAccount.contactProfileHeader.component}
          aria-label={`Profile for ${contactName}`}
        >
          {content}
        </Box>
      );
    }

    return (
      <Link
        to={ACCOUNT_PROFILE_ROUTE}
        className='fl-mobile-safe-area-support fl-contact-profile-header'
        data-testid={TestIds.feat.account.userAccount.contactProfileHeader.component}
        aria-label={`View profile for ${contactName}`}
      >
        {content}
      </Link>
    );
  }
};

export default ContactProfileHeader;
